/**
 * PublicPlanScreen — vista pública del plan (lo que el cliente del entrenador
 * abre desde su móvil al recibir el link `https://aetherlite.nilabs.co/p/abc123`).
 *
 * Características:
 *   - Mobile-first (probada mentalmente a 375px).
 *   - Read-only: re-utiliza el componente `<SectionView readonly />` del editor
 *     para garantizar paridad visual entre lo que ve el coach al diseñar y el
 *     resultado final del cliente.
 *   - Aplica el branding del coach al contenedor (CSS vars `--al-pub-primary`
 *     y `--al-pub-accent`). Si el coach no configuró colores, usa los defaults
 *     violeta de la marca.
 *   - Estados: loading skeleton, 404 / 410 / 500 con EmptyState + reintento.
 *   - Footer con marca de agua "Hecho con AetherLite" cuando coach.watermark
 *     === true (tier free) — linkado al landing.
 *
 * NO importa CatalogProvider ni TweaksPanel — la vista pública debe ser ligera
 * (los GIFs ya vienen embebidos como URLs en cada RoutineExercise.gifUrl).
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { ApiError } from "../api/client";
import { getPublicPlan, type PublicPlan } from "../api/plans";
import { Icon } from "../lib/Icon";
import { EmptyState } from "../lib/components";
import { SectionView } from "./plan/sections/SectionView";
import { PlanCalendar } from "./plan/PlanCalendar";
import { getTemplate } from "../types/templates";
import { SECTION_ICONS, SECTION_LABELS, type PlanBrand, type PlanSection, type RoutineWeek } from "../types/plan";
import { fontStack, ensurePlanFontsLoaded } from "../lib/fontStack";
import { HealthDisclaimer } from "../lib/HealthDisclaimer";

interface PublicPlanScreenProps {
  shortCode: string;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: PublicPlan }
  | { kind: "error"; status: number; message: string };

export function PublicPlanScreen({ shortCode }: PublicPlanScreenProps) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const data = await getPublicPlan(shortCode);
      setState({ kind: "ok", data });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 500;
      const message = e instanceof Error ? e.message : "Error inesperado";
      setState({ kind: "error", status, message });
    }
  }, [shortCode]);

  useEffect(() => {
    load();
    // Page title + meta theme-color reflejan branding del coach una vez cargado.
    return () => {
      document.title = "AetherLite";
    };
  }, [load]);

  // Aplica branding del coach al body cuando llega data (también restaura
  // theme-color/title del meta tag).
  useEffect(() => {
    if (state.kind !== "ok") return;
    const coach = state.data.coach;
    document.title = `${state.data.plan_name} · ${coach.name}`;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta && coach.brand_color_primary) {
      themeMeta.setAttribute("content", coach.brand_color_primary);
    }
  }, [state]);

  if (state.kind === "loading") {
    return <PublicLoadingSkeleton />;
  }

  if (state.kind === "error") {
    return <PublicErrorScreen status={state.status} onRetry={load} />;
  }

  return <PublicPlanContent data={state.data} />;
}

// ---------------------------------------------------------------------------
// Contenido principal
// ---------------------------------------------------------------------------

function PublicPlanContent({ data }: { data: PublicPlan }) {
  const { coach, sections, plan_name, client_name, view_count } = data;

  // Garantizar fuentes extra cargadas (Playfair/Montserrat/Oswald) por si el
  // preset del plan usa alguna.
  useEffect(() => {
    ensurePlanFontsLoaded();
  }, []);

  // Estilo del plan (Feature 3): el `brand` persistido manda. Si el plan no lo
  // tiene (planes legacy), se cae al criterio anterior — derivar el tema oscuro
  // de la paleta del template.
  const planBrand = data.brand;
  const tpl = getTemplate(data.template);
  const isDarkPlan = planBrand?.theme
    ? planBrand.theme === "dark"
    : tpl
    ? tpl.palette.bg.toLowerCase().startsWith("#0")
    : false;

  const primary = planBrand?.primary || coach.brand_color_primary || "#7c3aed";
  const accent = planBrand?.accent || coach.brand_color_accent || "#ec4899";
  const densityClass =
    planBrand?.density === "compact"
      ? "al-density-compact"
      : planBrand?.density === "spacious"
      ? "al-density-spacious"
      : "";

  // CSS vars de branding del coach + preset del plan. Fallback a violeta AetherLite.
  const cssVars: CSSProperties = {
    // CSS vars públicas (prefix al-pub-) + override de las globales del editor
    // para que SectionView (que lee --primary, --accent-magenta, --al-accent)
    // pinte con la marca del coach sin tocar el resto de la app.
    ["--al-pub-primary" as any]: primary,
    ["--al-pub-accent" as any]: accent,
    ["--primary" as any]: primary,
    ["--accent-magenta" as any]: accent,
    ["--al-accent" as any]: accent,
    // Tema dark: invierte fondo/tinta de las secciones (refuerza .al-plan-dark).
    ["--al-bg" as any]: isDarkPlan ? "#0a0a0a" : "#ffffff",
    ["--al-ink" as any]: isDarkPlan ? "#f4f4f5" : "#0a0a0a",
  };
  // Tipografía del preset — solo override si el plan la define.
  if (planBrand?.displayFont) {
    (cssVars as any)["--font-display"] = fontStack(planBrand.displayFont);
  }
  if (planBrand?.bodyFont) {
    (cssVars as any)["--font-body"] = fontStack(planBrand.bodyFont);
  }

  // Construir un PlanBrand a partir del coach + preset del plan para que
  // SectionView pinte logos + nombres + colores correctos.
  const brand: PlanBrand = {
    name: coach.name,
    tagline: coach.tagline || "ENTRENADOR PERSONAL",
    logoLetters: (coach.brand_letters || coach.name || "AL").slice(0, 2).toUpperCase(),
    logoUrl: coach.brand_logo_url,
    primary,
    accent,
    ig: coach.instagram || undefined,
    web: coach.web || undefined,
    theme: isDarkPlan ? "dark" : "light",
    // Watermark: si el plan tiene preferencia explícita persistida (post-sesión 5),
    // usarla. Sino, default = ON cuando hay logo del coach. Espejo de la lógica
    // en brandFromUser() — sin esto, SectionPage leía undefined y NUNCA renderizaba
    // watermark en el link público aunque el coach lo viera en el editor.
    watermark: planBrand?.watermark ?? !!coach.brand_logo_url,
  };

  // En public preview NO ofrecemos onChange — pasamos un noop. SectionView
  // respeta `readonly` y no dispara cambios, pero TS pide la prop.
  const noop = () => {};

  // Filtrar solo secciones habilitadas (las disabled no deben verse).
  const enabledSections = (sections as PlanSection[]).filter((s) => s && s.enabled !== false);

  // Track de la sección visible para resaltar el link del sidebar.
  const [activeSection, setActiveSection] = useState<string | null>(
    enabledSections[0]?.type ?? null,
  );
  useEffect(() => {
    if (enabledSections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const t = (e.target as HTMLElement).dataset.section;
            if (t) setActiveSection(t);
          }
        }
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: 0 },
    );
    enabledSections.forEach((s) => {
      const el = document.getElementById(`sec-${s.type}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledSections.length]);

  return (
    <div
      className={
        "al-pub-root" +
        (isDarkPlan ? " al-plan-dark" : "") +
        (densityClass ? " " + densityClass : "")
      }
      style={cssVars}
    >
      <PublicHeader coach={coach} />

      <div className="al-pub-body">
        <aside className="al-pub-nav" aria-label="Secciones del plan">
          <div className="al-pub-nav-eyebrow">EN ESTE PLAN</div>
          <ul className="al-pub-nav-list">
            {enabledSections.map((section) => {
              const subs = subItemsFor(section);
              return (
                <li key={section.id}>
                  <a
                    href={`#sec-${section.type}`}
                    className={section.type === activeSection ? "is-active" : ""}
                  >
                    <Icon name={SECTION_ICONS[section.type]} size={14} />
                    <span>{SECTION_LABELS[section.type] || section.type}</span>
                  </a>
                  {subs.length > 0 && (
                    <ul className="al-pub-nav-sub">
                      {subs.map((d) => (
                        <li key={d.id}>
                          <a href={`#day-${d.id}`}>{d.label}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        <main className="al-pub-main">
          <WelcomeCard
            clientName={client_name}
            planName={plan_name}
            coachName={coach.name}
          />

          {(() => {
            const clientSec = (sections as PlanSection[]).find((s) => s?.type === "client");
            const routineSec = (sections as PlanSection[]).find((s) => s?.type === "routine");
            const startDate = (clientSec?.data as any)?.startDate || "";
            const endDate = (clientSec?.data as any)?.endDate || "";
            const weeks = ((routineSec?.data as any)?.weeks || []) as RoutineWeek[];
            if (!startDate || weeks.length === 0) return null;
            return <PlanCalendar startDate={startDate} endDate={endDate} weeks={weeks} />;
          })()}

          <div className="al-pub-sections">
            {enabledSections.map((section) => (
              <article
                key={section.id}
                id={`sec-${section.type}`}
                data-section={section.type}
                className="al-pub-section-wrap"
              >
                <SectionView
                  section={section}
                  brand={brand}
                  onChange={noop}
                  readonly
                />
              </article>
            ))}
          </div>
        </main>
      </div>

      <PublicFooter watermark={coach.watermark} viewCount={view_count} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header sticky con branding del coach
// ---------------------------------------------------------------------------

interface PublicHeaderProps {
  coach: PublicPlan["coach"];
}

function PublicHeader({ coach }: PublicHeaderProps) {
  const initials = (coach.brand_letters || coach.name || "AL")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="al-pub-header">
      <div className="al-pub-header-inner">
        <div className="al-pub-coach">
          {coach.brand_logo_url ? (
            <img
              src={coach.brand_logo_url}
              alt={coach.name}
              className="al-pub-coach-logo"
              loading="eager"
            />
          ) : coach.picture_url ? (
            <img
              src={coach.picture_url}
              alt={coach.name}
              className="al-pub-coach-logo"
              loading="eager"
            />
          ) : (
            <div className="al-pub-coach-mark" aria-hidden="true">
              {initials}
            </div>
          )}
          <div className="al-pub-coach-text">
            <div className="al-pub-coach-name">{coach.name}</div>
            {coach.tagline ? (
              <div className="al-pub-coach-tag">{coach.tagline}</div>
            ) : null}
          </div>
        </div>

        <div className="al-pub-coach-links">
          {coach.instagram ? (
            <a
              href={instagramUrl(coach.instagram)}
              target="_blank"
              rel="noopener noreferrer"
              className="al-pub-link"
              aria-label="Instagram del entrenador"
              title="Instagram"
            >
              <Icon name="user" size={16} />
            </a>
          ) : null}
          {coach.web ? (
            <a
              href={ensureHttp(coach.web)}
              target="_blank"
              rel="noopener noreferrer"
              className="al-pub-link"
              aria-label="Web del entrenador"
              title="Web"
            >
              <Icon name="search" size={16} />
            </a>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/**
 * Sub-items del sidebar para una sección. Para la rutina expone los días de
 * la primera semana (todos comparten estructura tipo); para la dieta expone
 * los 7 días directamente. Sin sub-items para el resto.
 */
function subItemsFor(section: PlanSection): Array<{ id: string; label: string }> {
  if (section.type === "routine") {
    const data: any = section.data;
    const days = data?.weeks?.[0]?.days || [];
    return days
      .filter((d: any) => d && d.id && d.label)
      .map((d: any) => ({ id: d.id, label: d.label }));
  }
  if (section.type === "diet") {
    const data: any = section.data;
    const days = data?.days || [];
    return days
      .filter((d: any) => d && d.id && d.label)
      .map((d: any) => ({ id: d.id, label: d.label }));
  }
  return [];
}

function instagramUrl(handle: string): string {
  const clean = handle.replace(/^@/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//, "");
  return `https://instagram.com/${clean}`;
}

function ensureHttp(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

// ---------------------------------------------------------------------------
// Welcome card
// ---------------------------------------------------------------------------

interface WelcomeCardProps {
  clientName: string | null;
  planName: string;
  coachName: string;
}

function WelcomeCard({ clientName, planName, coachName }: WelcomeCardProps) {
  const greeting = clientName ? `Hola ${clientName} 👋` : "Hola 👋";
  return (
    <section className="al-pub-welcome">
      <div className="al-pub-welcome-eyebrow">TU PLAN</div>
      <h1 className="al-pub-welcome-title">{greeting}</h1>
      <div className="al-pub-welcome-sub">{planName}</div>
      <div className="al-pub-welcome-coach">
        Tu coach: <strong>{coachName}</strong>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer (marca de agua + view counter)
// ---------------------------------------------------------------------------

function PublicFooter({ watermark, viewCount }: { watermark: boolean; viewCount: number }) {
  return (
    <footer className="al-pub-footer">
      <HealthDisclaimer audience="client" />
      {watermark ? (
        <a className="al-pub-watermark" href="/" target="_blank" rel="noopener noreferrer">
          Hecho con <strong>AetherLite</strong> · Crea el tuyo
        </a>
      ) : (
        <span className="al-pub-watermark al-pub-watermark-quiet">
          Hecho con <strong>AetherLite</strong>
        </span>
      )}
      <span className="al-pub-views" title="Visitas a este link">
        {viewCount} {viewCount === 1 ? "visita" : "visitas"}
      </span>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Loading & error
// ---------------------------------------------------------------------------

function PublicLoadingSkeleton() {
  return (
    <div className="al-pub-root">
      <header className="al-pub-header">
        <div className="al-pub-header-inner">
          <div className="al-pub-coach">
            <div className="al-pub-skel al-pub-skel-circle" />
            <div>
              <div className="al-pub-skel al-pub-skel-line" style={{ width: 140 }} />
              <div className="al-pub-skel al-pub-skel-line" style={{ width: 90, marginTop: 6 }} />
            </div>
          </div>
        </div>
      </header>
      <main className="al-pub-main">
        <div className="al-pub-welcome">
          <div className="al-pub-skel al-pub-skel-line" style={{ width: 80 }} />
          <div className="al-pub-skel al-pub-skel-line" style={{ width: 200, height: 28, marginTop: 10 }} />
          <div className="al-pub-skel al-pub-skel-line" style={{ width: 160, marginTop: 8 }} />
        </div>
        <div className="al-pub-sections">
          {[1, 2, 3].map((i) => (
            <div key={i} className="al-pub-skel al-pub-skel-block" />
          ))}
        </div>
      </main>
    </div>
  );
}

function PublicErrorScreen({ status, onRetry }: { status: number; onRetry: () => void }) {
  let title: string;
  let body: string;
  let icon = "x";
  let showRetry = false;

  if (status === 404) {
    title = "Este plan no está disponible";
    body =
      "El link que abriste no es válido o el entrenador lo eliminó. Pídele un link nuevo a tu coach.";
  } else if (status === 410) {
    title = "Este link ha expirado";
    body = "Tu coach configuró una expiración para este link. Pídele uno nuevo y volvé a abrirlo.";
  } else {
    title = "No pudimos cargar tu plan";
    body =
      "Tuvimos un problema técnico al traer la información de tu coach. Reintentá en unos segundos.";
    icon = "swap";
    showRetry = true;
  }

  return (
    <div className="al-pub-root al-pub-error-root">
      <header className="al-pub-header">
        <div className="al-pub-header-inner">
          <div className="al-pub-coach">
            <div className="al-pub-coach-mark" aria-hidden="true">AL</div>
            <div className="al-pub-coach-text">
              <div className="al-pub-coach-name">AetherLite</div>
              <div className="al-pub-coach-tag">Planes para entrenadores</div>
            </div>
          </div>
        </div>
      </header>
      <main className="al-pub-main al-pub-error-main">
        <EmptyState
          icon={icon}
          title={title}
          body={body}
          action={
            showRetry ? (
              <button className="btn-primary" onClick={onRetry} style={{ marginTop: 12 }}>
                Reintentar
              </button>
            ) : (
              <a href="/" className="btn-secondary" style={{ marginTop: 12, display: "inline-flex" }}>
                Ir a AetherLite
              </a>
            )
          }
        />
      </main>
    </div>
  );
}
